import { z } from "zod";
import { Label } from "components/label";
import { Controller, useForm } from "react-hook-form";
import { HttpError, useOne } from "@refinedev/core";
import { zodResolver } from "@hookform/resolvers/zod";
import { SwitchButton } from "components/switchButton";
import Button from "components/button";
import { Link } from "react-router-dom";
import Select from "components/select";
import { get } from "lodash";
import { IAttractionQRCodeForm } from "interfaces";
import { useState } from "react";
import PreviewModal from "components/previewModal";
import Textarea from "components/textarea";

export interface BlogFormProps {
  onSubmit: (data: IAttractionQRCodeForm) => void;
}

const AttractionQRCodeSchema = z.object({
  is_active: z.boolean(),
  event_id: z.string().min(1, "This field is required"),
  product_id: z.string().min(1, "This field is required"),
  codes: z.string().min(1, "This field is required"),
});

export const AttractionQRCodeForm = ({ onSubmit }: BlogFormProps) => {
  const [previewImageModalData, setPreviewImageModalData] = useState({
    isOpen: false,
    image: ""
  });

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<IAttractionQRCodeForm, HttpError, IAttractionQRCodeForm>({
    defaultValues: {
      codes: "",
      event_id: "",
      is_active: true,
      product_id: ""
    },
    resolver: zodResolver(AttractionQRCodeSchema),
  });

  const {
    data: productIds,
    isFetching: isFetchingProductIds
  } = useOne<any[]>({
    resource: `content/events/${watch("event_id")}/tickets/dropdown`,
    id: "",
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!watch("event_id"),
    } as any,
  });

  const {
    data: eventIds,
    isLoading: isLoadingEventIds,
  } = useOne<any>({
    resource: `content/dropdown/events?v=2&event_type=attractions`,
    id: "",
    meta: {
      resource: "data",
    },
  });

  const _submit = (values: any) => {
    const submitValues = { ...values };
    submitValues.codes = submitValues.codes.split("\n");
    onSubmit(submitValues);
  };

  const eventIdsArr = (eventIds?.data ?? []).map((event: any) => (
    { value: event.id, label: event.name_en }
  ));

  const productIdsArr = (productIds?.data ?? []).map((product: any) => (
    { value: product.id, label: product.type_name_en }
  ));

  return (
    <div className="w-full ">
      <div>
        <div className="flex flex-col gap-y-2 sm:gap-y-4">
          <div className="w-full box-container">
            <div className="grid gap-x-4 gap-y-6 sm:grid-cols-10 lg:gap-x-6">
              <div className="sm:col-span-2">
                <Label required id="is_active" name="Status" />
                <Controller
                  control={control}
                  name="is_active"
                  defaultValue={true}
                  render={({ field: { onChange, value = true, ref } }) => (
                    <SwitchButton checked={value} onChange={onChange} />
                  )}
                />
              </div>
              <div className="sm:col-span-4">
                <Label required id="event_id" name="Event name" />
                <Controller
                  control={control}
                  name={"event_id"}
                  render={({ field: { onChange, value = "", ref } }) => {
                    return (
                      <Select
                        key="event_id"
                        inputRef={ref}
                        options={eventIdsArr}
                        isLoading={isLoadingEventIds}
                        id={`event_id`}
                        placeholder="Select Event name"
                        defaultValue={
                          eventIdsArr.find(
                            (selected: { label: string; value: any }) =>
                              selected.value === value
                          ) ?? ""
                        }
                        value={eventIdsArr.find(
                          (selected: { label: string; value: any }) =>
                            selected.value === value
                        )}
                        onChange={(selected: { label: string; value: any }) => {
                          onChange(selected?.value);
                          setValue("product_id", "")
                        }}
                        error={get(errors, `event_id`, "")}
                      />
                    );
                  }}
                />
                {get(errors, `event_id`, "") ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {get(errors, `event_id.message`, "")}
                    </span>
                  </p>
                ) : null}
              </div>
              <div className="sm:col-span-4">
                <Label id="product_id" name="Ticket name" required />
                <Controller
                  control={control}
                  name="product_id"
                  render={({ field: { onChange, value = "", ref } }) => {
                    return (
                      <Select
                        key={`product_id__${watch("event_id")}`}
                        inputRef={ref}
                        options={productIdsArr}
                        isLoading={isFetchingProductIds}
                        id="product_id"
                        placeholder="Select Ticket name"
                        isClearable={true}
                        defaultValue={
                          productIdsArr.find(
                            (selected: { label: string; value: any }) =>
                              selected.value === value
                          ) ?? ""
                        }
                        value={productIdsArr.find(
                          (selected: { label: string; value: any }) =>
                            selected.value === value
                        )}
                        onChange={(selected: { label: string; value: string }, {action} : any) => {
                          if (action === "clear") {
                            onChange("");
                          } else {
                            onChange(selected?.value);
                          }
                        }}
                        error={get(errors, `product_id`, "")}
                      />
                    );
                  }}
                />
                {get(errors, `product_id`, "") ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {get(errors, `product_id.message`, "")}
                    </span>
                  </p>
                ) : null}
              </div>
              <div className="col-span-full">
                <Label id="codes" name="Code" required />
                <Controller
                  control={control}
                  name="codes"
                  render={({ field: { onChange, value = "", ref } }) => {
                    return (
                      <Textarea
                        id="codes"
                        className="!font-mono"
                        placeholder="Input code"
                        error={get(errors, `codes`, "")}
                        onChange={onChange}
                        value={value}
                      />
                    );
                  }}
                />
                {get(errors, `codes`, "") ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {get(errors, `codes.message`, "")}
                    </span>
                  </p>
                ) : null}
              </div>

            </div>
          </div>
          <div className="w-full px-4 py-4 sm:px-6 bg-white sticky bottom-0 z-[2] mt-auto">
            <div className="flex items-center justify-end gap-x-4">
              <Link to={`/events/attraction-qr-code`}>
                <Button className="w-20 bg-white text-brand-500 border border-solid border-brand-500 hover:bg-gray-50 active:bg-gray-100">
                  Cancel
                </Button>
              </Link>
              <Button className="w-20" type="submit" onClick={handleSubmit(_submit)}>
                Save
              </Button>
            </div>
          </div>
        </div>
      </div>
      <PreviewModal 
        open={previewImageModalData.isOpen} 
        onClose={() => setPreviewImageModalData({
          ...previewImageModalData,
          isOpen: false
        })} 
        data={previewImageModalData.image}
      />
    </div>
  );
};